import { useRef, useState } from 'react';
import { FieldErrors, getApiProblem, notifyProblem } from '../utils/form-feedback';

export function useFormFeedback({ inline = true }: { inline?: boolean } = {}) {
  const [errors, setErrors] = useState<FieldErrors>({});
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const locked = useRef(false);

  const validate = (next: FieldErrors) => {
    const filtered = Object.fromEntries(Object.entries(next).filter(([, value]) => value));
    setErrors(filtered);
    setMessage(Object.keys(filtered).length ? 'Please complete or correct the highlighted fields.' : '');
    notifyProblem(null);
    return Object.keys(filtered).length === 0;
  };

  const execute = async (action: () => Promise<unknown>, explain?: (error: unknown) => string) => {
    if (locked.current) return false;
    locked.current = true;
    setBusy(true);
    setMessage('');
    setErrors({});
    notifyProblem(null);
    try {
      await action();
      return true;
    } catch (error) {
      const problem = getApiProblem(error);
      const readable = explain ? explain(error) : problem.message;
      setErrors(problem.fields);
      setMessage(readable);
      notifyProblem(inline ? null : readable);
      return false;
    } finally {
      locked.current = false;
      setBusy(false);
    }
  };

  const fieldProps = (field: string) => ({ name: field, error: !!errors[field], helperText: errors[field] });
  return { errors, message, busy, validate, execute, fieldProps };
}
